import { Injectable } from '@angular/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { BehaviorSubject } from 'rxjs';

export interface PushNotificationNotification {
  title: string;
  body: string;
  data: any;
}

export interface PushNotificationAction {
  actionId: string;
  inputValue: string;
}

export interface PushNotificationState {
  token: string;
  error: string;
  notification: PushNotificationNotification;
  action: PushNotificationAction;
}

export const defaultPushNotificationState: PushNotificationState = {
  token: '',
  error: '',
  notification: {
    title: '',
    body: '',
    data: null
  },
  action: {
    actionId: '',
    inputValue: ''
  }
};

@Injectable({
  providedIn: 'root'
})
export class PushNotificationService {
  private state$: BehaviorSubject<PushNotificationState> = new BehaviorSubject<PushNotificationState>(defaultPushNotificationState);

  get state() {
    return this.state$.asObservable();
  }

  setup() {
    console.info('Setting up push notifications...');
    (async () => {
      // 0. Check for existing permissions
      const permStatus = await PushNotifications.checkPermissions();
      if (permStatus.receive !== 'granted') {
        // 1. Request permissions
        const result = await PushNotifications.requestPermissions();
        if (result.receive !== 'granted') {
          console.error('Push notification permission denied');
          return;
        }
      }
      // 2. Register for notifications
      await PushNotifications.register();

      // 3. Handle registration
      PushNotifications.addListener('registrationError', error => {
        console.error('Push notification registration error: ', error);
        this.state$.next({
          ...this.state$.getValue(),
          error: error.error
        });
        // Handle error
      });

      PushNotifications.addListener('registration', token => {
        console.log('Ionic Push notification registration token: ', token.value);
        // Store the token for sending notifications later
        this.state$.next({
          ...this.state$.getValue(),
          token: token.value
        });
      });

      // 4. Handle incoming notifications
      PushNotifications.addListener('pushNotificationReceived', notification => {
        console.log('Push notification received: ', notification);
        this.state$.next({
          ...this.state$.getValue(),
          notification: {
            title: notification.title,
            body: notification.body,
            data: notification.data
          }
        });
      });

      // 5. Handle notification action
      PushNotifications.addListener('pushNotificationActionPerformed', notification => {
        console.log('Push notification action performed: ', notification.actionId, notification.inputValue);
        this.state$.next({
          ...this.state$.getValue(),
          action: {
            actionId: notification.actionId,
            inputValue: notification.inputValue
          }
        });
      });
    })();
  }

  // Call this method to update the push notification token on the server
  updatePushNotificationToken() {
    const token = this.state$.getValue().token;
    if (!token) {
      console.error('No push notification token available');
      return;
    }
    // TODO: Implement the logic to send the token to your server
    console.log('Updating push notification token on server: ', token);
  }
}
